import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getCustomers, createCustomer } from '../../../app/api';
import { useTranslation } from '../../../app/LanguageContext';

function getCustomerOptionLabel(option) {
  if (typeof option === 'string') return option;
  return (option.name || '') + (option.email ? ' – ' + option.email : '');
}

export function CustomerAutocomplete({
  value,
  onChange,
  onError,
  label,
  size = 'small',
  fullWidth = true,
  disabled = false,
}) {
  const { translate } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [customerInputText, setCustomerInputText] = useState('');
  const skipEnsureNextBlurRef = useRef(false);

  const loadCustomers = useCallback(async () => {
    try {
      const resp = await getCustomers();
      setCustomers(resp?.data?.customers || []);
    } catch (_) {
      setCustomers([]);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    if (!value) return;
    const sel = customers.find((c) => c.id === value);
    if (sel) {
      setCustomerInputText(getCustomerOptionLabel(sel));
    }
  }, [value, customers]);

  const handleChange = async (_, option) => {
    if (option == null) {
      skipEnsureNextBlurRef.current = true;
      setCustomerInputText('');
      onChange('', null);
      return;
    }
    let customerId = '';
    let customer = null;
    if (typeof option === 'object') {
      customerId = option.id || '';
      customer = option;
      setCustomerInputText(getCustomerOptionLabel(option));
    } else if (typeof option === 'string' && option.trim()) {
      try {
        const customerRes = await createCustomer({
          name: option.trim(),
          email: '',
          phone: '',
          document: '',
          address: '',
        });
        customerId = customerRes.data?.id || '';
        await loadCustomers();
        customer = { id: customerId, name: option.trim() };
        setCustomerInputText(option.trim());
      } catch (err) {
        onError?.(err.message);
        return;
      }
    } else {
      setCustomerInputText('');
    }
    onChange(customerId, customer);
  };

  const ensureCustomerOnBlur = useCallback(async () => {
    const input = (customerInputText || '').trim();
    if (!input) {
      onChange('', null);
      return;
    }
    if (value) {
      const sel = customers.find((c) => c.id === value);
      if (sel) {
        if (
          getCustomerOptionLabel(sel) === customerInputText ||
          sel.name.trim().toLowerCase() === input.toLowerCase()
        ) {
          return;
        }
      } else {
        return;
      }
    }
    const existing = customers.find(
      (c) => c.name.trim().toLowerCase() === input.toLowerCase()
    );
    if (existing) {
      onChange(existing.id, existing);
      setCustomerInputText(getCustomerOptionLabel(existing));
      return;
    }
    try {
      const customerRes = await createCustomer({
        name: input,
        email: '',
        phone: '',
        document: '',
        address: '',
      });
      const newId = customerRes.data?.id;
      if (!newId) return;
      await loadCustomers();
      onChange(newId, { id: newId, name: input });
      setCustomerInputText(input);
    } catch (err) {
      onError?.(err.message);
    }
  }, [customerInputText, customers, value, onChange, onError, loadCustomers]);

  const resolvedLabel = label ?? translate('orders.select_customer');

  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={customers}
      getOptionLabel={getCustomerOptionLabel}
      isOptionEqualToValue={(opt, val) => {
        if (val == null) return opt == null;
        if (typeof val === 'string') return typeof opt === 'string' && val === opt;
        if (typeof val === 'object' && val?.id != null) {
          return typeof opt === 'object' && opt?.id === val.id;
        }
        return false;
      }}
      componentsProps={{
        clearIndicator: {
          onMouseDown: (e) => {
            e.preventDefault();
          },
        },
      }}
      value={
        customers.find((c) => c.id === value) ??
        (customerInputText.trim() ? customerInputText : null)
      }
      onChange={handleChange}
      onInputChange={(event, newInputValue, reason) => {
        setCustomerInputText(newInputValue ?? '');
        if (reason === 'clear' || (newInputValue ?? '').trim() === '') {
          skipEnsureNextBlurRef.current = true;
          onChange('', null);
          return;
        }
        skipEnsureNextBlurRef.current = false;
        if (value) {
          const sel = customers.find((c) => c.id === value);
          if (sel) {
            const labelText = getCustomerOptionLabel(sel);
            if (newInputValue !== labelText && newInputValue !== sel.name) {
              onChange('', null);
            }
          }
        }
      }}
      inputValue={customerInputText}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          label={resolvedLabel}
          size={size}
          onBlur={(e) => {
            params.inputProps?.onBlur?.(e);
            window.setTimeout(() => {
              if (skipEnsureNextBlurRef.current) {
                skipEnsureNextBlurRef.current = false;
                return;
              }
              ensureCustomerOnBlur();
            }, 0);
          }}
        />
      )}
    />
  );
}
